import { useState, useEffect, useCallback } from 'react';
import { API } from '@/lib/constants';
import {
  Users, Send, Zap, CreditCard, Shield, LogOut, Radio,
  Loader2, Check, AlertCircle, Mail, Trash2, Download, ChevronDown
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

function AdminLogin({ onLogin }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      if (!res.ok) { setError('Invalid password'); setLoading(false); return; }
      const data = await res.json();
      sessionStorage.setItem('admin_token', data.token);
      sessionStorage.setItem('admin_password', password);
      onLogin(data.token);
    } catch {
      setError('Connection error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#060e1a]">
      <form onSubmit={handleSubmit} className="w-full max-w-sm rounded-2xl border border-white/10 bg-[#0a1929] p-8 shadow-[0_20px_60px_rgba(0,0,0,0.5)]">
        <div className="mb-6 flex items-center gap-3">
          <Shield className="h-8 w-8 text-[#3498DB]" />
          <div>
            <h1 className="text-xl font-bold text-white">Admin Panel</h1>
            <p className="text-xs text-white/40">AA Impact Dashboard</p>
          </div>
        </div>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Enter admin password"
          data-testid="admin-password-input"
          className="mb-4 w-full rounded-xl border border-white/10 bg-[#0B1E31] px-4 py-3 text-sm text-white placeholder:text-white/25 focus:border-[#3498DB]/40 focus:outline-none"
          autoFocus
        />
        {error ? <p className="mb-3 flex items-center gap-2 text-xs text-[#E74C3C]" data-testid="admin-login-error"><AlertCircle className="h-3 w-3" /> {error}</p> : null}
        <Button type="submit" disabled={loading || !password} data-testid="admin-login-btn" className="w-full rounded-xl bg-[#3498DB] py-3 text-white hover:bg-[#2e8cc0]">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Sign In'}
        </Button>
      </form>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, color }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-[rgba(13,34,56,0.92)] p-5">
      <div className="flex items-center gap-3 mb-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl" style={{ backgroundColor: `${color}15` }}>
          <Icon className="h-4 w-4" style={{ color }} />
        </div>
        <p className="text-xs uppercase tracking-[0.16em] text-white/40">{label}</p>
      </div>
      <p className="font-mono text-3xl font-bold text-white">{value}</p>
      {sub ? <p className="mt-1 text-xs text-white/35">{sub}</p> : null}
    </div>
  );
}

export default function AdminPanel() {
  const [token, setToken] = useState(() => sessionStorage.getItem('admin_token') || '');
  const [stats, setStats] = useState(null);
  const [subscribers, setSubscribers] = useState([]);
  const [events, setEvents] = useState([]);
  const [selectedRaceId, setSelectedRaceId] = useState('');
  const [sendStatus, setSendStatus] = useState(null);
  const [sending, setSending] = useState(false);
  const [raceMode, setRaceMode] = useState(false);
  const [togglingMode, setTogglingMode] = useState(false);

  const [refreshing, setRefreshing] = useState(false);
  const [refreshResult, setRefreshResult] = useState(null);

  const headers = { 'X-Admin-Token': token };

  const forceRefreshAll = async () => {
    setRefreshing(true);
    setRefreshResult(null);
    try {
      const password = sessionStorage.getItem('admin_password') || '';
      const res = await fetch(`${API}/f1/force-refresh?password=${encodeURIComponent(password)}`, { method: 'POST' });
      const data = await res.json();
      setRefreshResult(data);
      await fetchAll();
    } catch (err) {
      setRefreshResult({ status: 'error', message: err.message });
    } finally {
      setRefreshing(false);
    }
  };

  const fetchAll = useCallback(async () => {
    if (!token) return;
    try {
      const [statsRes, subsRes, eventsRes] = await Promise.all([
        fetch(`${API}/admin/dashboard-stats`, { headers }),
        fetch(`${API}/admin/subscribers`, { headers }),
        fetch(`${API}/f1/events`),
      ]);
      if (statsRes.status === 401) { setToken(''); sessionStorage.removeItem('admin_token'); return; }
      const [s, sub, ev] = await Promise.all([statsRes.json(), subsRes.json(), eventsRes.json()]);
      setStats(s);
      setSubscribers(sub.subscribers || []);
      setRaceMode(s.race_weekend_mode);
      const allEvents = [...(ev.events?.completed || []), ...(ev.events?.active || [])];
      setEvents(allEvents);
      if (allEvents.length && !selectedRaceId) setSelectedRaceId(String(allEvents[0].race_id));
    } catch { /* ignore */ }
  }, [token, headers, selectedRaceId]);

  useEffect(() => { fetchAll(); }, [token]);

  const toggleRaceMode = async () => {
    setTogglingMode(true);
    try {
      await fetch(`${API}/race-weekend-mode?enabled=${!raceMode}`, { method: 'POST' });
      setRaceMode(!raceMode);
      await fetchAll();
    } catch { /* ignore */ } finally { setTogglingMode(false); }
  };

  const sendReport = async () => {
    if (!selectedRaceId) return;
    setSending(true);
    setSendStatus(null);
    try {
      const res = await fetch(`${API}/admin/send-race-report?race_id=${selectedRaceId}`, { method: 'POST', headers });
      const data = await res.json();
      setSendStatus(data);
    } catch (err) {
      setSendStatus({ message: `Error: ${err.message}`, sent: 0, errors: [] });
    } finally { setSending(false); }
  };

  const removeSubscriber = async (email) => {
    try {
      await fetch(`${API}/subscribers/${encodeURIComponent(email)}`, { method: 'DELETE' });
      await fetchAll();
    } catch { /* ignore */ }
  };

  const downloadFile = async (endpoint, filename) => {
    try {
      const res = await fetch(`${API}/${endpoint}`, { headers });
      if (!res.ok) return;
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch { /* ignore */ }
  };

  const logout = () => { sessionStorage.removeItem('admin_token'); sessionStorage.removeItem('admin_password'); setToken(''); };

  if (!token) return <AdminLogin onLogin={setToken} />;

  return (
    <div className="min-h-screen bg-[#060e1a] text-white">
      <div className="mx-auto max-w-6xl px-6 py-8">
        <div className="mb-8 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="h-7 w-7 text-[#3498DB]" />
            <div>
              <h1 className="text-2xl font-bold">Dashboard Admin</h1>
              <p className="text-xs text-white/40">AA Impact F1 x Sustainability</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" className="rounded-xl border border-white/10 bg-white/[0.04] px-4 py-2 text-sm text-white/60 hover:text-white" data-testid="admin-back-to-dashboard">Back to Dashboard</a>
            <button onClick={logout} data-testid="admin-logout-btn" className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] px-4 py-2 text-sm text-white/60 hover:text-white">
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>

        {stats ? (
          <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="admin-stats-grid">
            <StatCard icon={Users} label="Active Subscribers" value={stats.subscribers?.active || 0} sub={`${stats.subscribers?.total || 0} total`} color="#3498DB" />
            <StatCard icon={CreditCard} label="Credits Used Today" value={stats.credits?.current_usage || 0} sub={`${stats.credits?.remaining_today || 0} remaining`} color="#E67E22" />
            <StatCard icon={Zap} label="Completed GPs" value={stats.events?.completed || 0} sub={`${stats.events?.active || 0} active`} color="#2A9D8F" />
            <StatCard icon={Radio} label="Race Mode" value={raceMode ? 'ON' : 'OFF'} sub={raceMode ? 'Auto-reverts in 3 days' : 'Conservation mode'} color={raceMode ? '#E74C3C' : '#6b8299'} />
          </div>
        ) : null}

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Send Race Report */}
          <div className="rounded-2xl border border-white/10 bg-[rgba(13,34,56,0.92)] p-6" data-testid="admin-send-report-section">
            <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold"><Send className="h-5 w-5 text-[#2A9D8F]" /> Send Race Report</h2>
            <p className="mb-4 text-xs text-white/40">Select a completed GP and send the race summary to all active subscribers.</p>
            <div className="mb-4 relative">
              <select
                value={selectedRaceId}
                onChange={(e) => setSelectedRaceId(e.target.value)}
                data-testid="admin-select-race"
                className="w-full appearance-none rounded-xl border border-white/10 bg-[#0B1E31] px-4 py-3 pr-10 text-sm text-white focus:border-[#3498DB]/40 focus:outline-none"
              >
                {events.map((ev) => (
                  <option key={ev.race_id} value={ev.race_id}>
                    R{ev.round_number} - {ev.grand_prix || ev.event_name} ({ev.country})
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/30 pointer-events-none" />
            </div>
            <Button onClick={sendReport} disabled={sending || !selectedRaceId} data-testid="admin-send-report-btn" className="w-full rounded-xl bg-[#2A9D8F] text-white hover:bg-[#248a7f]">
              {sending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending...</> : <><Mail className="mr-2 h-4 w-4" /> Send Report to {stats?.subscribers?.active || 0} Subscribers</>}
            </Button>
            {sendStatus ? (
              <div className={`mt-3 rounded-xl border p-3 text-sm ${sendStatus.sent > 0 ? 'border-[#2A9D8F]/30 bg-[#2A9D8F]/10 text-[#2A9D8F]' : 'border-[#E67E22]/30 bg-[#E67E22]/10 text-[#E67E22]'}`} data-testid="admin-send-status">
                <p className="flex items-center gap-2">{sendStatus.sent > 0 ? <Check className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />} {sendStatus.message}</p>
                {sendStatus.errors?.length > 0 ? <p className="mt-1 text-xs text-white/40">Errors: {sendStatus.errors.map((e) => e.email).join(', ')}</p> : null}
              </div>
            ) : null}
          </div>

          {/* Race Weekend Mode */}
          <div className="rounded-2xl border border-white/10 bg-[rgba(13,34,56,0.92)] p-6" data-testid="admin-race-mode-section">
            <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold"><Zap className="h-5 w-5 text-[#E74C3C]" /> Race Weekend Mode</h2>
            <p className="mb-4 text-xs text-white/40">
              When enabled, cache refresh intervals drop from 12 hours to 5 minutes for live data. Automatically reverts after 3 days.
            </p>
            <div className={`mb-4 rounded-xl border p-4 ${raceMode ? 'border-[#E74C3C]/30 bg-[#E74C3C]/10' : 'border-white/10 bg-white/[0.03]'}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-white">{raceMode ? 'RACE MODE ACTIVE' : 'Conservation Mode'}</p>
                  <p className="text-xs text-white/40">{raceMode ? 'Live refresh every 5 minutes' : 'Data refreshes every 12 hours'}</p>
                </div>
                <Badge className={raceMode ? 'border-[#E74C3C]/40 bg-[#E74C3C]/20 text-[#E74C3C]' : 'border-white/10 bg-white/5 text-white/50'}>{raceMode ? 'LIVE' : 'IDLE'}</Badge>
              </div>
            </div>
            <Button onClick={toggleRaceMode} disabled={togglingMode} data-testid="admin-toggle-race-mode-btn" className={`w-full rounded-xl ${raceMode ? 'bg-white/10 text-white hover:bg-white/15' : 'bg-[#E74C3C] text-white hover:bg-[#c0392b]'}`}>
              {togglingMode ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Zap className="mr-2 h-4 w-4" />}
              {raceMode ? 'Disable Race Mode' : 'Enable Race Mode'}
            </Button>

            <div className="mt-6">
              <h3 className="mb-3 text-sm font-semibold text-white/60">Quick Actions</h3>
              <div className="space-y-2">
                <button onClick={forceRefreshAll} disabled={refreshing} data-testid="admin-force-refresh-btn" className="flex w-full items-center gap-3 rounded-xl border border-[#E67E22]/30 bg-[#E67E22]/10 px-4 py-3 text-left text-sm text-[#E67E22] hover:bg-[#E67E22]/20 transition-colors disabled:opacity-50">
                  {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
                  {refreshing ? 'Refreshing all data...' : 'Force Refresh All Data'}
                </button>
                {refreshResult ? (
                  <div className={`rounded-lg border px-3 py-2 text-xs ${refreshResult.status === 'refreshed' ? 'border-[#2A9D8F]/30 bg-[#2A9D8F]/10 text-[#2A9D8F]' : 'border-[#E74C3C]/30 bg-[#E74C3C]/10 text-[#E74C3C]'}`}>
                    {refreshResult.status === 'refreshed' ? <Check className="mr-1 inline h-3 w-3" /> : <AlertCircle className="mr-1 inline h-3 w-3" />}
                    {refreshResult.status === 'refreshed' ? `Refreshed: ${refreshResult.events} events, ${refreshResult.sessions_refreshed} sessions` : refreshResult.detail || refreshResult.message || 'Error'}
                  </div>
                ) : null}
                <button onClick={() => downloadFile('download/asset-pack', 'AA_Impact_Dashboard_Asset_Pack.md')} data-testid="admin-download-asset-pack" className="flex w-full items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-left text-sm text-white/60 hover:bg-white/[0.06] hover:text-white transition-colors">
                  <Download className="h-4 w-4 text-[#3498DB]" /> Download Asset Pack
                </button>
                <button onClick={() => downloadFile('download/wix-guide', 'Wix_Embedding_Guide.md')} data-testid="admin-download-wix-guide" className="flex w-full items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-left text-sm text-white/60 hover:bg-white/[0.06] hover:text-white transition-colors">
                  <Download className="h-4 w-4 text-[#2A9D8F]" /> Download Wix Embedding Guide
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Subscribers Table */}
        <div className="mt-6 rounded-2xl border border-white/10 bg-[rgba(13,34,56,0.92)] p-6" data-testid="admin-subscribers-section">
          <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold"><Users className="h-5 w-5 text-[#3498DB]" /> Subscribers ({subscribers.filter((s) => s.active).length} active)</h2>
          {subscribers.length === 0 ? (
            <p className="text-sm text-white/40">No subscribers yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="pb-3 text-left text-[10px] uppercase tracking-[0.16em] text-white/35">Email</th>
                    <th className="pb-3 text-left text-[10px] uppercase tracking-[0.16em] text-white/35">Name</th>
                    <th className="pb-3 text-left text-[10px] uppercase tracking-[0.16em] text-white/35">Status</th>
                    <th className="pb-3 text-left text-[10px] uppercase tracking-[0.16em] text-white/35">Subscribed</th>
                    <th className="pb-3 text-right text-[10px] uppercase tracking-[0.16em] text-white/35">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {subscribers.map((sub) => (
                    <tr key={sub.email} className="border-b border-white/5" data-testid={`admin-subscriber-row-${sub.email}`}>
                      <td className="py-3 text-sm text-white">{sub.email}</td>
                      <td className="py-3 text-sm text-white/60">{sub.name || '-'}</td>
                      <td className="py-3">
                        <Badge className={sub.active ? 'border-[#2A9D8F]/30 bg-[#2A9D8F]/15 text-[#2A9D8F]' : 'border-white/10 bg-white/5 text-white/40'}>{sub.active ? 'Active' : 'Inactive'}</Badge>
                      </td>
                      <td className="py-3 text-xs text-white/40">{sub.subscribed_at ? new Date(sub.subscribed_at).toLocaleDateString() : '-'}</td>
                      <td className="py-3 text-right">
                        {sub.active ? (
                          <button onClick={() => removeSubscriber(sub.email)} className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs text-[#E74C3C] hover:bg-[#E74C3C]/10" data-testid={`admin-remove-subscriber-${sub.email}`}>
                            <Trash2 className="h-3 w-3" /> Remove
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
